import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
  Chip,
  Alert,
  Grid,
  Stack,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Schedule,
  CheckCircle,
  Close as CloseIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import assessmentService, { Assessment, AssessmentAttempt } from '../../services/assessmentService';
import AssessmentRunner from './AssessmentRunner';
import { format } from 'date-fns';

interface AssessmentCardProps {
  courseId: number;
  topicIds?: number[];
}

const AssessmentCard: React.FC<AssessmentCardProps> = ({ courseId, topicIds }) => {
  const [loading, setLoading] = useState(true);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [activeAssessment, setActiveAssessment] = useState<Assessment | null>(null);
  const [activeAttempt, setActiveAttempt] = useState<AssessmentAttempt | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAssessments();
  }, [courseId, topicIds]);

  const loadAssessments = async () => {
    setLoading(true);
    setError(null);
    try {
      const available = await assessmentService.getAvailableAssessments(courseId);
      // Filter by topicIds if provided
      const filtered = topicIds
        ? available.filter((a) => topicIds.some((tid) => a.topicIds.includes(tid)))
        : available;
      setAssessments(filtered);
    } catch (err: any) {
      setError(err.message || 'Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const handleStartAssessment = async (assessment: Assessment) => {
    try {
      const attempt = await assessmentService.startAssessment(assessment.id);
      setActiveAttempt(attempt);
      setActiveAssessment(assessment);
    } catch (err: any) {
      setError(err.message || 'Failed to start assessment');
    }
  };

  const handleCloseAssessment = () => {
    setActiveAssessment(null);
    setActiveAttempt(null);
    loadAssessments(); // Refresh list
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (assessments.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Assessments
          </Typography>
          <Alert severity="info">No assessments available for this course.</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Assessments
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Complete assessments to demonstrate your understanding and mark topics as complete.
          </Typography>

          <Grid container spacing={2}>
            {assessments.map((assessment) => (
              <Grid item xs={12} sm={6} md={4} key={assessment.id}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {assessment.title}
                    </Typography>
                    {assessment.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {assessment.description}
                      </Typography>
                    )}

                    <Stack spacing={1} sx={{ mb: 2 }}>
                      {assessment.scheduledDate && (
                        <Chip
                          icon={<Schedule />}
                          label={`Due: ${format(new Date(assessment.scheduledDate), 'MMM dd, yyyy')}`}
                          size="small"
                          color="primary"
                        />
                      )}
                      {assessment.durationMinutes && (
                        <Chip
                          icon={<Schedule />}
                          label={`${assessment.durationMinutes} min`}
                          size="small"
                        />
                      )}
                      <Chip
                        label={`Pass: ${assessment.passingScore}%`}
                        size="small"
                        color="success"
                      />
                      {assessment.questionCount && (
                        <Chip
                          label={`${assessment.questionCount} questions`}
                          size="small"
                        />
                      )}
                    </Stack>

                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => handleStartAssessment(assessment)}
                      disabled={assessment.status !== 'PUBLISHED'}
                    >
                      {assessment.status === 'PUBLISHED' ? 'Start Assessment' : 'Not Available'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {activeAssessment && activeAttempt && (
        <Dialog
          open={true}
          onClose={handleCloseAssessment}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { height: '90vh' },
          }}
        >
          <DialogContent sx={{ p: 0, position: 'relative' }}>
            <IconButton
              onClick={handleCloseAssessment}
              sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}
            >
              <CloseIcon />
            </IconButton>
            <AssessmentRunner
              assessment={activeAssessment}
              attempt={activeAttempt}
              onComplete={handleCloseAssessment}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default AssessmentCard;
